import { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';

import { Consumer, createConsumer } from '@rails/actioncable';
import { useMutation } from '@tanstack/react-query';

import { useAuth } from 'context/auth';
import { useEnvironment } from 'context/environment';
import { createWebsocketToken } from 'services/api/websocketToken';

interface ActionCableContextValue {
  consumer: Consumer | null;
}

const ActionCableContext = createContext<ActionCableContextValue>({
  consumer: null,
});

export const ActionCableProvider = ({ children }: PropsWithChildren) => {
  const { environment } = useEnvironment();
  const authContext = useAuth();
  const token = authContext?.token;
  const [consumer, setConsumer] = useState<Consumer | null>(null);
  const isInitializing = useRef(false);

  const { mutate: fetchWebsocketToken } = useMutation({
    mutationFn: createWebsocketToken,
    onSuccess: (websocketToken) => {
      if (!websocketToken || !environment?.OLIO_API_DOMAIN) {
        isInitializing.current = false;
        return;
      }

      const apiUrl = new URL(environment.OLIO_API_DOMAIN);
      const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${apiUrl.host}/cable`;

      const wsUrlWithToken = `${wsUrl}?token=${websocketToken}`;

      const newConsumer = createConsumer(wsUrlWithToken);
      newConsumer.connection.reopen = fetchWebsocketToken;

      if (consumer) consumer.disconnect();
      setConsumer(newConsumer);

      isInitializing.current = false;
    },
    onError: () => {
      isInitializing.current = false;
    },
  });

  // Initialize consumer when needed
  useEffect(() => {
    if (authContext?.loading || !environment?.OLIO_API_DOMAIN || !token || consumer || isInitializing.current) {
      return;
    }

    isInitializing.current = true;
    fetchWebsocketToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.OLIO_API_DOMAIN, token, authContext?.loading, consumer]);

  useEffect(() => {
    return () => {
      if (consumer) {
        consumer.disconnect();
      }
    };
  }, [consumer]);

  const value = { consumer };

  return <ActionCableContext value={value}>{children}</ActionCableContext>;
};

export const useActionCable = () => {
  const context = useContext(ActionCableContext);
  if (!context) {
    throw new Error('useActionCable must be used within ActionCableProvider');
  }
  return context;
};
